import { useEffect, useReducer } from "react";
import type { AbstractAgent } from "@ag-ui/client";

// Subscribes to the agent's lifecycle events and forces a re-render so
// consumers can read `agent.messages` / `agent.state` / `agent.isRunning`
// directly. Replaces CopilotKit's `useAgent` to avoid pulling in the chat-UI
// dependency chain (streamdown → katex), which we don't use.
//
// `agent.isRunning` flips to false only when the run's stream completes, and
// ag-ui's HttpAgent completes on socket close — not on the terminal
// RUN_FINISHED / RUN_ERROR event. With keep-alive that close lags by seconds,
// leaving the UI stuck "running". So on those events we detach the run to end
// the stream immediately; detach (not abortRun) keeps completion on the
// success path. onRunFinalized covers every other termination (cancel,
// unmount, network error).
export function useAgent(agent: AbstractAgent): AbstractAgent {
  const [, forceRerender] = useReducer((x: number) => x + 1, 0);
  useEffect(() => {
    let active = true;

    // Coalesce multiple synchronous notifications into a single React re-render.
    let batchScheduled = false;
    const batchedRerender = () => {
      if (!active || batchScheduled) return;
      batchScheduled = true;
      queueMicrotask(() => {
        batchScheduled = false;
        if (active) forceRerender();
      });
    };

    // The run does not complete on the terminal protocol event, so detach to
    // end the stream now instead of waiting for the socket to close.
    const finishRun = () => {
      void agent.detachActiveRun();
      batchedRerender();
    };

    const sub = agent.subscribe({
      onMessagesChanged: batchedRerender,
      onStateChanged: batchedRerender,
      onRunInitialized: batchedRerender,
      onRunFinishedEvent: finishRun,
      onRunErrorEvent: finishRun,
      onRunFinalized: batchedRerender,
    });
    return () => {
      active = false;
      sub.unsubscribe();
    };
  }, [agent]);
  return agent;
}
